import SignalList from './SignalList.jsx';
import { verdictMeta, formatLatency, countryFlag } from '../lib/format.js';
import { buildOpenUrl } from '../lib/api.js';

const VerdictBadge = ({ verdict }) => {
  const meta = verdictMeta[verdict] ?? { label: verdict, tone: 'muted' };
  return (
    <span className={`badge badge--${meta.tone}`}>
      <span className={`dot dot--${meta.tone}`} />
      {meta.label}
    </span>
  );
};

const Panel = ({ label, title, analysis }) => {
  if (!analysis) {
    return (
      <div className="compare__panel">
        <div className="compare__head">
          <span className="compare__label">{label}</span>
        </div>
        <p className="signals__empty">Not tested — no country was selected.</p>
      </div>
    );
  }

  return (
    <div className="compare__panel">
      <div className="compare__head">
        <div>
          <div className="compare__label">{label}</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        </div>
        <VerdictBadge verdict={analysis.verdict} />
      </div>

      <div className="kv">
        <span className="kv__key">Reachable</span>
        <span className="kv__value">{analysis.reachable ? 'yes' : 'no'}</span>
      </div>
      {analysis.reachable && (
        <>
          <div className="kv">
            <span className="kv__key">HTTP status</span>
            <span className="kv__value">{analysis.status}</span>
          </div>
          <div className="kv">
            <span className="kv__key">Response time</span>
            <span className="kv__value">{formatLatency(analysis.elapsedMs)}</span>
          </div>
        </>
      )}
      <div className="kv">
        <span className="kv__key">Block score</span>
        <span className="kv__value">{analysis.score} / 100</span>
      </div>
      {analysis.error && (
        <div className="kv">
          <span className="kv__key">Error</span>
          <span className="kv__value">{analysis.error.code}</span>
        </div>
      )}

      {analysis.chain?.length > 1 && (
        <div className="chain">
          <div className="chain__label">Redirect chain</div>
          {analysis.chain.map((hop, index) => (
            <div className="chain__hop" key={`${hop.url}-${index}`}>
              <span className="chain__status">{hop.rejected ? '⛔' : (hop.status ?? '—')}</span>
              <span className="chain__url" title={hop.url}>
                {hop.url}
              </span>
            </div>
          ))}
        </div>
      )}

      <SignalList signals={analysis.signals} />
    </div>
  );
};

export default function AnalysisResult({ result }) {
  const { summary, direct, via, url, requestedCountry } = result;

  // An interception outranks every other headline: the request never left the
  // country, so claiming anything about the site's own policy would be a guess -
  // and claiming a bypass would be flatly false.
  const { icon, headline } = summary.interceptedByIsp
    ? { icon: '📡', headline: 'Intercepted by your internet provider' }
    : !summary.isGeoBlocked
      ? { icon: '✅', headline: 'This link is not geo-restricted' }
      : summary.bypassWorks
        ? { icon: '🔓', headline: 'Geo-restriction confirmed — and bypassed' }
        : { icon: '🚫', headline: 'This link appears to be geo-restricted' };

  const openCountry = via?.node?.country ?? requestedCountry;
  const openHref = buildOpenUrl(url, openCountry, via?.node?.id);

  return (
    <div className="card">
      <div className="verdict">
        <span className="verdict__icon">{icon}</span>
        <div className="verdict__body">
          <span className="verdict__headline">{headline}</span>
          <span className="verdict__detail">{summary.recommendation}</span>
        </div>
      </div>

      <div className="compare">
        <Panel label="Direct request" title="From the gateway itself" analysis={direct} />
        <Panel
          label="Through exit node"
          title={
            via?.node
              ? `${countryFlag(via.node.country)} ${via.node.label}`
              : 'No exit node used'
          }
          analysis={via}
        />
      </div>

      <div className="open-bar">
        <a
          className="btn"
          href={openHref}
          target="_blank"
          rel="noreferrer noopener"
          aria-disabled={!openCountry}
          onClick={(event) => {
            if (!openCountry) event.preventDefault();
          }}
        >
          Open the page through the gateway ↗
        </a>
        {!openCountry && (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Pick a country above to enable this.
          </span>
        )}
        {summary.interceptedByIsp && (
          <span style={{ fontSize: 13, color: 'var(--warn)' }}>
            Opening it will show your provider's block page — no exit node can change that.
          </span>
        )}
      </div>
    </div>
  );
}
